<script lang="ts">
    import {Flex, Spacer} from 'shared/layouts';
    import {getCurrentLanguage, getLocalization} from 'src/core';
    import type {DateRangeInterval, IntervalString} from './date-range-util';
    import type {CalendarView} from 'shared/components/kp-calendar/types';
    import type {DateRangeGranularityType, DateRangeIntervalPartType} from './types';
    import {DateTime} from 'luxon';
    import {formatIntervalObjectToReadableForm} from './date-range-format-util';
    import {isDateInInterval, parseObjectToIntervalString, isIntervalInvalid} from './date-range-util';
    import {parseIntervalStringToObject} from './date-range-util';
    import {cleanup} from 'shared/utils/custom-utils';
    import {KpCalendarManager} from 'shared/components/kp-calendar/kp-calendar-manager';
    import {createEventDispatcher, onDestroy} from 'svelte';
    import {fly} from 'svelte/transition';
    import {getPaddedMonthDays, luxonDateFromCalendarDate} from 'shared/components/kp-calendar/utils';
    import KpIconButton from 'shared/ui-widgets/button/KpIconButton.svelte';
    import IntervalPartButton from './IntervalPartButton.svelte';
    import Loc from 'shared/components/kp-markdown/Loc.svelte';

    export let interval: IntervalString | null;
    export let granularity: DateRangeGranularityType = 'date-only';
    export let inPopoverMode = false;

    const dispatch = createEventDispatcher<{
        'interval-changed': IntervalString,
        'close-click': void
    }>();
    const localize = getLocalization();
    const currentLang = getCurrentLanguage();
    const parsedIntervalObject = parseIntervalStringToObject(interval);
    const dayNames = getLocalizedDayNames(currentLang);

    let from = parsedIntervalObject.from;
    let to = parsedIntervalObject.to;

    $: intervalObject = {from, to};
    $: isInvalid = isIntervalInvalid(intervalObject);

    let selecting: DateRangeIntervalPartType = 'from';

    const calendarManager = KpCalendarManager.createNavigableCalendar({
        type: 'monthView'
    });

    let calendarView: CalendarView;
    const calendarViewSubscription = calendarManager.getView$().subscribe((view) => calendarView = view);

    onDestroy(() => {
        cleanup(calendarViewSubscription);
    });

    function getCalendarLabel(view: CalendarView): string {
        const formatter = new Intl.DateTimeFormat(getCurrentLanguage(), {month: 'long'});
        const monthDate = new Date(view.year, view.month - 1, 1);
        return `${view.year} - ${formatter.format(monthDate)}`;
    }

    function getLocalizedDayNames(lang: string): string[] {
        const formatter = new Intl.DateTimeFormat(lang, {weekday: 'short'});
        const referenceDate = new Date(Date.UTC(2023, 0, 2)); // Monday, Jan 2, 2023 - reference date for Monday

        return Array.from({length: 7}, (_, i) => {
            const date = new Date(referenceDate);
            date.setDate(referenceDate.getDate() + i);
            return formatter.format(date).toUpperCase();
        });
    }

    function getFormattedIntervalString(intervalObj: DateRangeInterval): string {
        return formatIntervalObjectToReadableForm(intervalObj, localize, granularity);
    }

    const handleDateClick = (date: DateTime) => {
        if (selecting === 'from') {
            from = date;
        } else {
            to = date;
        }

        dispatch('interval-changed', parseObjectToIntervalString({from, to}));
    };

    const handleIntervalPartSelection = (event: CustomEvent<DateRangeIntervalPartType>) => {
        selecting = event.detail;
    };

    const handleSetInfinityBound = (event: CustomEvent<DateRangeIntervalPartType>) => {
        if (event.detail === 'from') {
            from = '-infinity';
        } else {
            to = 'infinity';
        }

        dispatch('interval-changed', parseObjectToIntervalString({from, to}));
    };

    const handleResetIntervalToEmpty = () => {
        from = undefined;
        to = undefined;
        selecting = 'from';
        dispatch('interval-changed', parseObjectToIntervalString({from, to}));
    };

    const handleClosePopoverClick = () => {
        dispatch('close-click');
    };
</script>

<div class="kp-date-range-picker">
    <div class="toolbar-container">
        <Flex direction="row" alignItems="center">
            <KpIconButton icon="angle-small-left" on:click={() => calendarManager.navigate(-1)}/>
            <Spacer direction="horizontal" size="s"/>
            <KpIconButton icon="angle-small-right" on:click={() => calendarManager.navigate(1)}/>
            <Spacer direction="horizontal" size="m"/>

            <span class="view-label">{getCalendarLabel(calendarView)}</span>

            <Spacer flex="1"/>
            <Spacer direction="horizontal" size="l"/>

            <IntervalPartButton selectedType={selecting}
                                type="from"
                                isInfinity={from === '-infinity'}
                                on:select={handleIntervalPartSelection}
                                on:infinity={handleSetInfinityBound}/>
            <Spacer direction="horizontal" size="s"/>
            <IntervalPartButton selectedType={selecting}
                                type="to"
                                isInfinity={to === 'infinity'}
                                on:select={handleIntervalPartSelection}
                                on:infinity={handleSetInfinityBound}/>

            <Spacer direction="horizontal" size="m"/>
            <KpIconButton buttonStyle="danger" icon="trash" on:click={handleResetIntervalToEmpty}/>

            {#if inPopoverMode}
                <Spacer direction="horizontal" size="m"/>
                <KpIconButton buttonStyle="no-background" icon="cross-circle" on:click={handleClosePopoverClick}/>
            {/if}
        </Flex>

        <Spacer direction="vertical" size="m"/>

        <Flex direction="row" alignItems="center">
            {localize(/* @kp-localization commons.SelectedDateRange */ 'commons.SelectedDateRange')}: {getFormattedIntervalString(intervalObject)}
        </Flex>
    </div>

    {#key calendarView.month}
        <div class="grid-calendar" in:fly="{{x: 20 * calendarView.navigateDirection, duration: 250}}">
            {#each getPaddedMonthDays(calendarView.days) as day, index (day)}
                {@const luxonDate = luxonDateFromCalendarDate(day, 'UTC')}
                {@const isStartOfInterval = DateTime.isDateTime(intervalObject.from) && luxonDate.hasSame(intervalObject.from, 'day')}
                {@const isEndOfInterval = DateTime.isDateTime(intervalObject.to) && luxonDate.hasSame(intervalObject.to, 'day')}
                {@const isInInterval = isDateInInterval(luxonDate, intervalObject)}
                {@const isToday = luxonDate.hasSame(DateTime.now(), 'day')}

                <button class="calendar-day"
                        on:click={() => handleDateClick(luxonDate)}
                        class:invalid-interval={isInvalid}
                        class:padding-day={day.padding}
                        class:highlighted={isInInterval}
                        class:start-or-end={isStartOfInterval || isEndOfInterval}
                        class:today={isToday}>

                    {#if index < 7}
                        <span class="day-name">{dayNames[index]}</span>
                    {/if}

                    {#if isStartOfInterval || isEndOfInterval}
                        {#if isStartOfInterval && isEndOfInterval}
                            <span class="start-end">
                                {localize(/* @kp-localization commons.From */ 'commons.From')}-{localize(/* @kp-localization commons.To */ 'commons.To')}
                            </span>
                        {:else}
                            <span class="start-end">
                                {#if isStartOfInterval}
                                    <Loc code="commons.From"/>
                                {:else}
                                    <Loc code="commons.To"/>
                                {/if}
                            </span>
                        {/if}
                    {/if}

                    <span class="date">{day.day}</span>
                </button>
            {/each}
        </div>
    {/key}
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .kp-date-range-picker {
        display: flex;
        flex-direction: column;
        gap: @spacing-ml;
    }

    .toolbar-container {
        display: flex;
        flex-direction: column;
        padding: @spacing-sm @spacing-m @spacing-ml;
        border-bottom: 1px solid @themed-border-default;

        .view-label {
            font-weight: 500;
        }
    }

    .grid-calendar {
        display: grid;
        margin: 0 @spacing-m @spacing-m;
        grid-template-columns: repeat(7, 1fr);

        .calendar-day {
            position: relative;
            width: 100%;
            height: 55px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            outline: none;
            border: none;
            background: none;
            cursor: pointer;
            isolation: isolate;

            --start-end-color: var(--accent-blue);

            .date {
                font-size: @font-size-small;
                font-weight: 500;
            }

            .day-name,
            .start-end {
                position: absolute;
                left: 50%;
                transform: translateX(-50%);
                color: @themed-text-muted;
                font-size: @font-size-xs;
                white-space: nowrap;
                text-align: center;
            }

            .day-name {
                top: 2px;
            }

            .start-end {
                bottom: 2px;
            }

            &:before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                width: 100%;
                height: 100%;
                background-color: transparent;
                transition: background-color 0.3s ease-in-out;
                z-index: -1;
            }

            &:hover {
                &:before {
                    background-color: @themed-panel-bg;
                }
            }

            &.invalid-interval {
                --start-end-color: var(--danger-red);
            }

            &.highlighted {
                &:before {
                    background-color: @themed-body-bg-blue-highlighted;
                }
            }

            &.start-or-end {
                color: white;

                &:before {
                    background-color: var(--start-end-color);
                }

                .day-name,
                .start-end {
                    color: white;
                    opacity: 0.75;
                }
            }

            &.padding-day {
                opacity: 0.5;
            }
        }
    }

    :global {
        .kp-date-range-picker .toolbar-container .kp-button {
            height: 30px;
            display: flex;
            align-items: center;
        }
    }
</style>